import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApiClient } from '../ApiClientProvider';
import { queryKeys } from '../keys';
import type { AvailableMethod, CarrierService, CarrierServicePayload } from '../types/services';

type Wrapped<T> = { data: T };

export function useCarrierServices() {
  const api = useApiClient();
  return useQuery<CarrierService[], Error>({
    queryKey: queryKeys.carrierServices.list(),
    queryFn: async ({ signal }) => {
      const response = await api.get<Wrapped<CarrierService[]>>('/admin/carrier-services', signal);
      return response.data;
    },
  });
}

/** Services are nested in each market's available-methods rows, so both caches go stale together. */
function invalidateServiceCaches(
  qc: ReturnType<typeof useQueryClient>,
  marketId?: number | null,
): void {
  void qc.invalidateQueries({ queryKey: queryKeys.carrierServices.all });
  // Enabling/disabling a method changes the config-health banner's "no active
  // delivery method" check, which is derived server-side and surfaced via /admin/me.
  void qc.invalidateQueries({ queryKey: queryKeys.me });
  if (marketId != null) {
    void qc.invalidateQueries({ queryKey: queryKeys.markets.availableMethods(marketId) });
  } else {
    void qc.invalidateQueries({ queryKey: queryKeys.markets.all });
  }
}

export function useCreateCarrierService() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<CarrierService, Error, CarrierServicePayload>({
    mutationFn: async (payload) => {
      const response = await api.post<Wrapped<CarrierService>>('/admin/carrier-services', payload);
      return response.data;
    },
    onSuccess: (service) => {
      invalidateServiceCaches(qc, service.market_id);
    },
  });
}

export function useUpdateCarrierService(id: number) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<CarrierService, Error, Partial<CarrierServicePayload>>({
    mutationFn: async (payload) => {
      const response = await api.patch<Wrapped<CarrierService>>(
        `/admin/carrier-services/${id}`,
        payload,
      );
      return response.data;
    },
    onSuccess: (service) => {
      invalidateServiceCaches(qc, service.market_id);
    },
  });
}

export function useDeleteCarrierService(marketId: number) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) => api.del(`/admin/carrier-services/${id}`),
    onSuccess: () => {
      invalidateServiceCaches(qc, marketId);
    },
  });
}

/** Optimistically flips `service.is_enabled` in the market's available-methods cache. */
export function useToggleCarrierService(marketId: number) {
  const api = useApiClient();
  const qc = useQueryClient();
  const methodsKey = queryKeys.markets.availableMethods(marketId);

  return useMutation<
    CarrierService,
    Error,
    { id: number; is_enabled: boolean },
    { previous: AvailableMethod[] | undefined }
  >({
    mutationFn: async ({ id, is_enabled }) => {
      const response = await api.patch<Wrapped<CarrierService>>(`/admin/carrier-services/${id}`, {
        is_enabled,
      });
      return response.data;
    },
    onMutate: async ({ id, is_enabled }) => {
      await qc.cancelQueries({ queryKey: methodsKey });
      const previous = qc.getQueryData<AvailableMethod[]>(methodsKey);
      qc.setQueryData<AvailableMethod[]>(methodsKey, (old) =>
        old?.map((method) =>
          method.service?.id === id
            ? { ...method, service: { ...method.service, is_enabled } }
            : method,
        ),
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      // Restore unconditionally - if onMutate threw before snapshotting, clearing the
      // cache (undefined) forces the onSettled refetch to re-establish server truth.
      qc.setQueryData(methodsKey, context?.previous);
    },
    onSettled: () => {
      invalidateServiceCaches(qc, marketId);
    },
  });
}

/**
 * Optimistically rewrites display priorities in the market's available-methods cache.
 * Takes the configured service ids in their new order; the backend writes dense
 * 0..n-1 priorities atomically (PATCH /admin/markets/{id}/method-order), so a partial
 * network failure can no longer leave torn priorities server-side.
 */
export function useReorderCarrierServices(marketId: number) {
  const api = useApiClient();
  const qc = useQueryClient();
  const methodsKey = queryKeys.markets.availableMethods(marketId);

  return useMutation<void, Error, number[], { previous: AvailableMethod[] | undefined }>({
    mutationFn: (orderedServiceIds) =>
      api.patch(`/admin/markets/${marketId}/method-order`, { service_ids: orderedServiceIds }),
    onMutate: async (orderedServiceIds) => {
      await qc.cancelQueries({ queryKey: methodsKey });
      const previous = qc.getQueryData<AvailableMethod[]>(methodsKey);
      const priorityById = new Map(orderedServiceIds.map((id, index) => [id, index]));
      qc.setQueryData<AvailableMethod[]>(methodsKey, (old) =>
        old?.map((method) => {
          const nextPriority = method.service ? priorityById.get(method.service.id) : undefined;
          return method.service && nextPriority !== undefined
            ? { ...method, service: { ...method.service, display_priority: nextPriority } }
            : method;
        }),
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      // Restore unconditionally - if onMutate threw before snapshotting, clearing the
      // cache (undefined) forces the onSettled refetch to re-establish server truth.
      qc.setQueryData(methodsKey, context?.previous);
    },
    onSettled: () => {
      invalidateServiceCaches(qc, marketId);
    },
  });
}
